/**
 * CreateSongFromImage — standalone "image to song" flow.
 *
 * Why a separate page (not a tab inside CreateSong)?
 *   - The image flow is intentionally minimal: drop an image, see the
 *     auto-generated caption, optionally tweak, pick a vibe, generate.
 *   - CreateSong is 2k+ lines with multi-step state — wedging this in
 *     would muddy both UIs. Standalone page = clearer UX, easier to
 *     iterate on, easier to instrument.
 *
 * Flow:
 *   1. User drops / picks an image → uploaded to HP server via
 *      /api/slideshow/upload-photo (existing endpoint, returns public URL).
 *   2. Frontend POSTs the URL to /api/songs/caption-image — Gemini 3
 *      Flash returns a musical caption + suggested genre/mood.
 *   3. Caption populates an editable textarea (always visible) so users
 *      can refine without hunting through hidden menus.
 *   4. Genre / Mood / Voice quick pickers (pre-filled from Gemini's
 *      suggestions when possible).
 *   5. Single "Generate Song" button → POST /api/songs/generate with
 *      engine="musicgpt", prompt=caption, image_url for provenance.
 */
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, apiFetch, getStoredToken } from '../App';
import MainNav from '../components/MainNav';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Image as ImageIcon, Loader2, RefreshCw, Sparkles, X, ArrowLeft } from 'lucide-react';

const MOODS = ['upbeat', 'romantic', 'melancholic', 'energetic', 'peaceful', 'playful', 'hopeful', 'nostalgic', 'dark'];
const GENRES = ['pop', 'rock', 'jazz', 'country', 'hip-hop', 'electronic', 'classical', 'acoustic', 'gospel', 'indie'];

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreateSongFromImage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [imageUrl, setImageUrl] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [captioning, setCaptioning] = useState(false);
  const [caption, setCaption] = useState('');
  const [genre, setGenre] = useState('');
  const [mood, setMood] = useState('');
  const [voice, setVoice] = useState('female');
  // Instrumental: when true, MusicGPT / SUNO skip vocals + lyrics entirely.
  const [instrumental, setInstrumental] = useState(false);
  // Engine: MusicGPT (default) or SUNO V5. No other versions —
  // legacy SUNO V4 / V4.5 are intentionally not offered here.
  const [engine, setEngine] = useState('musicgpt');
  const [generating, setGenerating] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);

  const reset = () => {
    setImageUrl(null);
    setImagePreview(null);
    setCaption('');
    setGenre('');
    setMood('');
    setVoice('female');
    setInstrumental(false);
    setEngine('musicgpt');
  };

  // Caption a single image URL with a strict timeout + one silent retry.
  // The Gemini call has been observed to occasionally hang silently for
  // 60+ seconds; a 30s client-side timeout + 1 retry covers that without
  // making the user wait through it manually.
  const captionWithTimeout = async (url, timeoutMs = 30000) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const { data } = await apiFetch('/songs/caption-image', {
        method: 'POST',
        body: JSON.stringify({ image_url: url }),
        signal: controller.signal,
      });
      return data;
    } finally {
      clearTimeout(timer);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file (JPG, PNG, WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or smaller');
      return;
    }

    // Local preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to HP server
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getStoredToken();
      const res = await fetch(`${API}/api/slideshow/upload-photo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!res.ok) {
        let msg = `Upload failed (${res.status})`;
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
        } catch { /* not JSON */ }
        throw new Error(msg);
      }
      const data = await res.json();
      const url = data.url;
      if (!url) throw new Error('Upload succeeded but no URL returned');
      setImageUrl(url);
      setUploading(false);

      // Auto-fire captioning
      await runCaptioning(url);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Upload failed');
      setUploading(false);
      setImagePreview(null);
    }
  };

  const runCaptioning = async (url) => {
    setCaptioning(true);
    let data = null;
    let lastErr = null;
    // Two attempts — covers the "first call hung silently" case the user hit.
    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        data = await captionWithTimeout(url, 30000);
        break;
      } catch (e) {
        lastErr = e;
        if (attempt < 2) {
          console.warn(`Caption attempt ${attempt} failed, retrying:`, e?.message);
        }
      }
    }
    if (data) {
      setCaption(data.caption || '');
      if (data.suggested_genre && GENRES.includes(data.suggested_genre)) {
        setGenre(data.suggested_genre);
      }
      if (data.suggested_mood && MOODS.includes(data.suggested_mood)) {
        setMood(data.suggested_mood);
      }
    } else {
      console.error(lastErr);
      const isAbort = lastErr?.name === 'AbortError' || /abort/i.test(lastErr?.message || '');
      toast.error(
        isAbort
          ? 'The vision model took too long. Hit Regenerate to try again, or type your own description below.'
          : (lastErr?.message || 'Caption failed — you can still type your own prompt below.')
      );
    }
    setCaptioning(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const generateSong = async () => {
    if (!caption.trim()) {
      toast.error('Please provide a description of the song you want.');
      return;
    }
    if (!imageUrl) {
      toast.error('Please upload an image first.');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await apiFetch('/songs/generate', {
        method: 'POST',
        body: JSON.stringify({
          prompt: caption.trim(),
          description: caption.trim(),
          mood: mood || 'upbeat',
          genre: genre || 'pop',
          voice,
          engine,
          instrumental,
          // SUNO V5 only (V4 / V4.5 intentionally not offered here);
          // ignored by the backend when engine='musicgpt'.
          suno_model: 'V5',
          image_url: imageUrl,
        }),
      });
      if (!data.song_id) throw new Error('No song ID returned');
      await refreshUser?.();
      toast.success("Song generation started! Usually 1-2 minutes — we'll notify you when it's ready.", { duration: 7000 });
      navigate(`/song/${data.song_id}`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to start song generation');
    } finally {
      setGenerating(false);
    }
  };

  const busy = uploading || captioning || generating;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 to-black text-white">
      <MainNav />
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-zinc-400 hover:text-white text-sm flex items-center gap-1 mb-4"
          data-testid="img2song-back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-amber-400" />
            Create Song from Image
          </h1>
          <p className="text-zinc-400 text-base">
            Upload a photo, painting, or scene — we&apos;ll describe it musically and write you a song.
          </p>
        </div>

        {/* STEP 1: image upload zone */}
        {!imagePreview ? (
          <div
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            className={`glass-card p-12 text-center cursor-pointer transition-all border-2 border-dashed ${dragOver ? 'border-amber-400 bg-amber-500/5' : 'border-white/15 hover:border-white/30'}`}
            data-testid="img2song-dropzone"
          >
            <ImageIcon className="h-14 w-14 mx-auto mb-4 text-zinc-500" />
            <p className="text-lg font-medium mb-1">Drop an image here or click to browse</p>
            <p className="text-sm text-zinc-500">JPG, PNG, or WEBP — up to 5 MB</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="img2song-file-input"
            />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Image preview with remove button */}
            <div className="glass-card p-4 relative">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative w-full sm:w-48 sm:h-48 flex-shrink-0 rounded-lg overflow-hidden bg-black/40">
                  <img
                    src={imagePreview}
                    alt="Source"
                    className="w-full h-full object-cover"
                    data-testid="img2song-preview"
                  />
                  {(uploading || captioning) && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-xs uppercase tracking-wider text-zinc-500">
                      {uploading ? 'Uploading…' : captioning ? 'Reading the image…' : 'Your image'}
                    </div>
                    <button
                      onClick={reset}
                      disabled={busy}
                      className="text-zinc-400 hover:text-rose-400 disabled:opacity-40"
                      title="Start over"
                      data-testid="img2song-reset"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    {captioning
                      ? 'Our vision model is describing your image…'
                      : imageUrl
                        ? 'Tweak the description below or just hit Generate.'
                        : 'Almost there…'}
                  </p>
                </div>
              </div>
            </div>

            {/* STEP 2: editable caption */}
            <div>
              <label className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-zinc-300">Song description</span>
                {imageUrl && !captioning && (
                  <button
                    onClick={() => runCaptioning(imageUrl)}
                    disabled={busy}
                    className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1 disabled:opacity-40"
                    data-testid="img2song-recaption"
                  >
                    <RefreshCw className="h-3 w-3" /> Regenerate
                  </button>
                )}
              </label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                disabled={captioning || generating}
                placeholder={captioning ? 'Generating…' : 'A description of the song this image inspires…'}
                rows={4}
                className="w-full bg-black/40 border border-white/10 rounded-lg p-3 text-sm text-zinc-100 outline-none focus:border-amber-400/60 resize-none disabled:opacity-60"
                data-testid="img2song-caption"
              />
            </div>

            {/* STEP 3: engine + quick pickers */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-2">
                Engine
                <span className="ml-2 normal-case tracking-normal text-[10px] text-zinc-600">
                  (only MusicGPT and SUNO V5 are supported)
                </span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEngine('musicgpt')}
                  disabled={busy}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${engine === 'musicgpt' ? 'border-amber-400/70 bg-amber-500/10' : 'border-white/10 bg-black/30 hover:border-white/25'} disabled:opacity-60`}
                  data-testid="img2song-engine-musicgpt"
                >
                  <div className="text-sm font-semibold text-white flex items-center gap-2">
                    MusicGPT
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200 uppercase tracking-wider">Default</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">Faster, image-aware vocals</div>
                </button>
                <button
                  type="button"
                  onClick={() => setEngine('suno')}
                  disabled={busy}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${engine === 'suno' ? 'border-amber-400/70 bg-amber-500/10' : 'border-white/10 bg-black/30 hover:border-white/25'} disabled:opacity-60`}
                  data-testid="img2song-engine-suno"
                >
                  <div className="text-sm font-semibold text-white">SUNO V5</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">Cinematic, richer production</div>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Genre</label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  disabled={busy}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-400/60 disabled:opacity-60"
                  data-testid="img2song-genre"
                >
                  <option value="">Auto</option>
                  {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">Mood</label>
                <select
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  disabled={busy}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-400/60 disabled:opacity-60"
                  data-testid="img2song-mood"
                >
                  <option value="">Auto</option>
                  {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">
                  Voice {instrumental && <span className="normal-case text-[10px] text-zinc-600">(disabled)</span>}
                </label>
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  disabled={busy || instrumental}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 outline-none focus:border-amber-400/60 disabled:opacity-40"
                  data-testid="img2song-voice"
                >
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                  <option value="duet">Duet</option>
                </select>
              </div>
            </div>

            {/* Instrumental toggle — when on, MusicGPT/SUNO produce
                a vocal-free track and the lyrics-backfill cron skips
                this song entirely. */}
            <label
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${instrumental ? 'border-amber-400/70 bg-amber-500/10' : 'border-white/10 bg-black/30 hover:border-white/25'} ${busy ? 'opacity-60 cursor-not-allowed' : ''}`}
              data-testid="img2song-instrumental-toggle"
            >
              <input
                type="checkbox"
                checked={instrumental}
                onChange={(e) => setInstrumental(e.target.checked)}
                disabled={busy}
                className="h-4 w-4 accent-amber-400"
                data-testid="img2song-instrumental-checkbox"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">Instrumental (no vocals)</div>
                <div className="text-[11px] text-zinc-400">
                  Skip the vocals entirely — pure music. Useful for background tracks.
                </div>
              </div>
            </label>

            {/* Generate */}
            <Button
              size="lg"
              onClick={generateSong}
              disabled={busy || !caption.trim() || !imageUrl}
              className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-base py-6 disabled:opacity-50"
              data-testid="img2song-generate"
            >
              {generating ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Starting your song…</>
              ) : captioning ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Reading image…</>
              ) : (
                <><Sparkles className="h-5 w-5 mr-2" /> Generate Song</>
              )}
            </Button>

            <p className="text-xs text-zinc-500 text-center">
              Powered by Gemini 3 Flash vision + {engine === 'suno' ? 'SUNO V5' : 'MusicGPT'} — generation usually takes 1–2 minutes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
